/**
 * A small SQL tokenizer, for the trace (PRD FR-5.2).
 *
 * The trace is the product's trust claim, so the SQL under an answer has to be
 * readable at a glance rather than a wall of monospace. Highlighting is done
 * here rather than with a syntax-highlighting library for two reasons: the
 * colours have to come from the theme tokens like everything else, and the
 * relations in a FROM or JOIN have to become links into the schema browser —
 * which needs the parse, not just the paint.
 *
 * "Relation" here means precisely what follows FROM or JOIN and resolves
 * against the names the server recorded for that step. A column that happens to
 * be called `orders` is not a link.
 */

export type TokenKind =
  | "keyword"
  | "function"
  | "string"
  | "number"
  | "comment"
  | "operator"
  | "relation"
  | "identifier"
  | "plain";

export interface SqlToken {
  text: string;
  kind: TokenKind;
  /** The schema-qualified relation this token links to, when it is one. */
  relation?: string;
}

const KEYWORDS = new Set([
  "select", "from", "where", "group", "by", "order", "having", "limit", "offset", "join",
  "inner", "left", "right", "full", "outer", "cross", "on", "as", "and", "or", "not", "in",
  "is", "null", "like", "ilike", "between", "case", "when", "then", "else", "end", "distinct",
  "with", "union", "all", "except", "intersect", "asc", "desc", "over", "partition", "using",
  "cast", "exists", "any", "some", "values", "explain", "describe", "show", "pragma", "top",
  "fetch", "next", "rows", "only", "nulls", "first", "last", "filter", "within", "interval",
]);

const FUNCTIONS = new Set([
  "count", "sum", "avg", "min", "max", "round", "coalesce", "nullif", "abs", "ceil", "floor",
  "date_trunc", "to_char", "strftime", "date_format", "extract", "concat", "substr",
  "substring", "length", "lower", "upper", "trim", "now", "current_date", "date", "datetime",
  "format", "convert", "rank", "dense_rank", "row_number", "lag", "lead", "percentile_cont",
  "median", "stddev", "variance", "cume_dist", "ntile",
]);

const WORD = /[A-Za-z_][A-Za-z0-9_$]*/y;
const NUMBER = /\d+(?:\.\d+)?/y;
const WHITESPACE = /\s+/y;

function bare(name: string): string {
  return name.replace(/^[["`]|[\]"`]$/g, "").toLowerCase();
}

/** Match a written name against the relations the server resolved. */
function resolve(written: string, relations: string[]): string | undefined {
  const parts = written.split(".").map(bare);
  const tail = parts[parts.length - 1] ?? "";
  const full = parts.join(".");
  return relations.find((relation) => {
    const known = relation.toLowerCase();
    return known === full || known === tail || known.split(".").pop() === tail;
  });
}

export function tokenizeSql(sql: string, relations: string[] = []): SqlToken[] {
  const tokens: SqlToken[] = [];
  let index = 0;
  // Set by FROM/JOIN, cleared by the next thing that is not part of a name.
  let expectRelation = false;

  const push = (text: string, kind: TokenKind, relation?: string) => {
    tokens.push(relation ? { text, kind, relation } : { text, kind });
  };

  while (index < sql.length) {
    const rest = sql.slice(index);

    if (rest.startsWith("--")) {
      const end = sql.indexOf("\n", index);
      const stop = end < 0 ? sql.length : end;
      push(sql.slice(index, stop), "comment");
      index = stop;
      continue;
    }
    if (rest.startsWith("/*")) {
      const end = sql.indexOf("*/", index + 2);
      const stop = end < 0 ? sql.length : end + 2;
      push(sql.slice(index, stop), "comment");
      index = stop;
      continue;
    }
    if (rest[0] === "'") {
      let cursor = index + 1;
      while (cursor < sql.length) {
        if (sql[cursor] === "'" && sql[cursor + 1] === "'") cursor += 2;
        else if (sql[cursor] === "'") break;
        else cursor += 1;
      }
      push(sql.slice(index, Math.min(cursor + 1, sql.length)), "string");
      index = Math.min(cursor + 1, sql.length);
      expectRelation = false;
      continue;
    }

    WHITESPACE.lastIndex = index;
    const space = WHITESPACE.exec(sql);
    if (space) {
      push(space[0], "plain");
      index = WHITESPACE.lastIndex;
      continue;
    }

    // A relation reference: `schema.table`, quoted in any of four dialects.
    if (expectRelation) {
      const name = /^(?:(?:"[^"]+"|`[^`]+`|\[[^\]]+\]|[A-Za-z_][A-Za-z0-9_$]*)\.)*(?:"[^"]+"|`[^`]+`|\[[^\]]+\]|[A-Za-z_][A-Za-z0-9_$]*)/.exec(
        rest,
      );
      if (name) {
        const written = name[0];
        const match = resolve(written, relations);
        push(written, match ? "relation" : "identifier", match);
        index += written.length;
        expectRelation = false;
        continue;
      }
      if (rest[0] === "(") expectRelation = false;
    }

    if (rest[0] === '"' || rest[0] === "`" || rest[0] === "[") {
      const closer = rest[0] === "[" ? "]" : rest[0];
      const end = sql.indexOf(closer, index + 1);
      const stop = end < 0 ? sql.length : end + 1;
      push(sql.slice(index, stop), "identifier");
      index = stop;
      continue;
    }

    NUMBER.lastIndex = index;
    const number = NUMBER.exec(sql);
    if (number && /[\d]/.test(rest[0] ?? "")) {
      push(number[0], "number");
      index = NUMBER.lastIndex;
      continue;
    }

    WORD.lastIndex = index;
    const word = WORD.exec(sql);
    if (word) {
      const lowered = word[0].toLowerCase();
      if (KEYWORDS.has(lowered)) {
        push(word[0], "keyword");
        expectRelation = lowered === "from" || lowered === "join";
      } else if (FUNCTIONS.has(lowered) && sql[WORD.lastIndex] === "(") {
        push(word[0], "function");
      } else {
        push(word[0], "identifier");
      }
      index = WORD.lastIndex;
      continue;
    }

    push(sql[index] ?? "", /[=<>+\-*/%|!]/.test(sql[index] ?? "") ? "operator" : "plain");
    index += 1;
  }

  return tokens;
}
